<template>
  <button type="button" class="marker-row" :class="[`is-${layout}`, { 'is-selected': selected, 'is-disabled': !buttonEnabled }]"
    :disabled="!buttonEnabled" @click="locate">
    <span class="marker-thumb" :class="{ 'is-loading': !thumbSettled }">
      <img v-show="imageSrc && thumbLoaded" :src="imageSrc" class="thumb-image" alt="" @load="thumbLoaded = true"
        @error="onThumbError" />
      <v-icon v-if="thumbSettled && !imageSrc" size="18" color="#4a5a60">mdi-image-outline</v-icon>
    </span>

    <span class="marker-content">
      <span class="marker-label">{{ markerCount }} markers</span>
      <span v-if="areaName" class="marker-sub">{{ areaName }}</span>
    </span>
  </button>
</template>

<script setup>
import { defineEmits, defineOptions, defineProps, ref, computed, watch } from "vue";
import organizationSasService from "@/services/OrganizationSasService";

defineOptions({ name: "HotSpotListItem" })

const emits = defineEmits(["onLocateHotSpotMarker"])

const props = defineProps({
  hotSpot: Object,
  asset: Object,
  areaName: { type: String, default: '' },
  buttonEnabled: { type: Boolean, default: true },
  layout: { type: String, default: 'list' },   // 'list' | 'grid'
  selected: { type: Boolean, default: false },
})

const locate = () => {
  if (!props.buttonEnabled) return
  emits("onLocateHotSpotMarker", props.hotSpot)
}

const markerCount = computed(() => props.hotSpot?.numberOfMarkers || 0)

const imageSrc = ref(null)
const thumbLoaded = ref(false)      // <img> has decoded
const thumbSettled = ref(false)     // signing resolved (image or placeholder)

function onThumbError() {
  imageSrc.value = null
  thumbLoaded.value = false
  thumbSettled.value = true
}

watch(
  () => props.hotSpot,
  async (newVal) => {
    thumbLoaded.value = false
    thumbSettled.value = false
    imageSrc.value = null
    if (!newVal) { thumbSettled.value = true; return }
    try {
      imageSrc.value = await organizationSasService.getSignedUrl(newVal.thumbnailPath)
    } catch {
      imageSrc.value = null
    } finally {
      thumbSettled.value = true
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.marker-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  text-align: left;
  font: inherit;
  cursor: pointer;
  color: inherit;
}
.marker-row.is-disabled { cursor: default; }

.marker-thumb {
  position: relative;
  flex: 0 0 auto;
  width: clamp(40px, calc(28px + 0.9vw), 52px);
  height: clamp(32px, calc(22px + 0.75vw), 42px);
  border-radius: 8px;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.thumb-image { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Shimmer while the thumbnail is being signed / decoded. */
.marker-thumb.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  animation: mk-shimmer 1.2s ease-in-out infinite;
}
@keyframes mk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .marker-thumb.is-loading::after { animation: none; } }

.marker-content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.marker-label {
  font-size: clamp(0.82rem, calc(0.72rem + 0.12vw), 0.9rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.marker-sub {
  font-size: 0.74rem;
  color: #8aa0aa;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Grid layout (thumbnail on top, meta below) ── */
.marker-row.is-grid {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 10px;
}
.marker-row.is-grid .marker-thumb {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
}
.marker-row.is-grid .marker-content { flex: 0 0 auto; }
</style>
